import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LegalHeader from '../components/LegalHeader';

const CookiePolicy = () => {
  const { t } = useLanguage();

  const lastUpdatedDate = '2 de junio de 2026';
  const s = t('cookiePolicy.sections');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <LegalHeader />

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <div className="flex justify-center items-center space-x-4 mb-4">
            <img
              src="/farm-logo.png"
              alt="FARM Logo"
              className="w-12 h-12 rounded-xl"
            />
            <h1 className="text-3xl font-bold text-gray-900">{t('cookiePolicy.title')}</h1>
          </div>
          <p className="text-gray-600">{t('cookiePolicy.subtitle')}</p>
        </div>
      </div>

      {/* Contenido */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">

          {/* Introducción */}
          <section>
            <p className="text-gray-700 leading-relaxed">{t('cookiePolicy.intro.p1')}</p>
            <p className="text-gray-700 leading-relaxed mt-4">{t('cookiePolicy.intro.p2')}</p>
          </section>

          {/* Qué son las cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.whatAreCookies.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.whatAreCookies.p1}</p>
              <p>{s.whatAreCookies.p2}</p>
            </div>
          </section>

          {/* Cookies que utilizamos */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.cookiesWeUse.title}</h2>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{s.cookiesWeUse.goodNews.title}</h3>
              <p className="text-gray-700 mb-4">{s.cookiesWeUse.goodNews.content}</p>
            </div>

            <div className="mt-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.cookiesWeUse.technical.title}</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 p-4 text-left font-semibold">{s.cookiesWeUse.technical.tableHeaders.provider}</th>
                      <th className="border border-gray-300 p-4 text-left font-semibold">{s.cookiesWeUse.technical.tableHeaders.purpose}</th>
                      <th className="border border-gray-300 p-4 text-left font-semibold">{s.cookiesWeUse.technical.tableHeaders.type}</th>
                      <th className="border border-gray-300 p-4 text-left font-semibold">{s.cookiesWeUse.technical.tableHeaders.duration}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.cookiesWeUse.technical.rows.map((row, i) => (
                      <tr key={i}>
                        <td className="border border-gray-300 p-4">{row.provider}</td>
                        <td className="border border-gray-300 p-4">{row.purpose}</td>
                        <td className="border border-gray-300 p-4">{row.type}</td>
                        <td className="border border-gray-300 p-4">{row.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Google Fonts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.googleFonts.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.googleFonts.p1}</p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{s.googleFonts.infoSent.title}</h3>
                <ul className="list-disc pl-6 space-y-1">
                  {s.googleFonts.infoSent.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>

              <p>
                {s.googleFonts.p2}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 ml-1">
                  https://policies.google.com/privacy
                </a>
              </p>
            </div>
          </section>

          {/* Lo que NO hacemos */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.whatWeNotDo.title}</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4"><strong>{s.whatWeNotDo.intro}</strong></p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                {s.whatWeNotDo.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </section>

          {/* Newsletter */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.newsletter.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.newsletter.p1}</p>
              <ul className="list-disc pl-6 space-y-2">
                {s.newsletter.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </section>

          {/* Aplicación móvil */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.mobileApp.title}</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <p className="text-gray-700">
                <strong>Importante:</strong> {s.mobileApp.content}
                <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800 ml-1">
                  {s.mobileApp.link}
                </a>.
              </p>
            </div>
          </section>

          {/* Gestión de cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.manageCookies.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.manageCookies.intro}</p>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">{s.manageCookies.browserSettings.title}</h3>
              <p>{s.manageCookies.browserSettings.intro}</p>
              <ul className="list-disc pl-6 space-y-2">
                {s.manageCookies.browserSettings.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">{s.manageCookies.browserLinks.title}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800">Google Chrome</a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/es/kb/cookies-informacion-que-los-sitios-web-guardan-en-" target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800">Mozilla Firefox</a>
                </li>
                <li>
                  <a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800">Safari</a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/es-es/help/17442/windows-internet-explorer-delete-manage-cookies" target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800">Microsoft Edge</a>
                </li>
              </ul>

              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-6">
                <p className="text-red-800"><strong>Nota:</strong> {s.manageCookies.warning}</p>
              </div>
            </div>
          </section>

          {/* Base legal */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.legalBasis.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.legalBasis.p1}</p>
              <p>{s.legalBasis.p2}</p>
            </div>
          </section>

          {/* Actualizaciones */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.updates.title}</h2>
            <p className="text-gray-700 leading-relaxed">{s.updates.content}</p>
          </section>

          {/* Contacto */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.contact.title}</h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2">{s.contact.intro}</p>
              <div className="space-y-2">
                <p className="text-gray-700">
                  <strong>{s.contact.email}:</strong>
                  <a href="mailto:support@farmway.dev" className="text-blue-600 hover:text-blue-800 ml-1">
                    support@farmway.dev
                  </a>
                </p>
                <p className="text-gray-700">
                  <strong>{s.contact.addressLabel}:</strong> Gran Vía Carles III, 98, 10º, 08028 Barcelona, España
                </p>
              </div>
            </div>
          </section>

          {/* Fecha de actualización */}
          <section className="border-t pt-6">
            <p className="text-sm text-gray-500">
              {t('cookiePolicy.effectiveDate').replace('{date}', lastUpdatedDate)}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {t('cookiePolicy.lastUpdated').replace('{date}', lastUpdatedDate)}
            </p>
          </section>

        </div>

        {/* Botón de volver */}
        <div className="mt-8 text-center">
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
          >
            {t('cookiePolicy.backButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
